/**
 * Everspring — Header section behaviour
 *
 * Sticky hide-on-scroll, mobile slide-stack navigation, dropdown focus
 * management, mobile search toggle, typewriter placeholder animation.
 *
 * Extracted from sections/es-header.liquid (2026-04-11) so it can be
 * cached across pages instead of re-parsed on every pageview.
 */
(function () {
  'use strict';

  // Double-init guard — theme editor can hot-reload sections which would
  // otherwise stack document-level event listeners.
  if (window.__esHeaderInit) return;
  window.__esHeaderInit = true;

  var header   = document.getElementById('es-header');
  var nav      = document.getElementById('es-mobile-nav');
  var toggle   = document.getElementById('es-mobile-nav-toggle');
  var overlay  = document.getElementById('es-mobile-nav-overlay');

  /* ── Hide-on-scroll-down / show-on-scroll-up (mobile only) ──────────
     Toggles `body.es-header-hidden` based on scroll direction. CSS in
     this file handles the actual visual translation of the header
     wrapper. The collection cat-tabs strip listens for the same body
     class to slide its `top` from var(--es-header-bottom) to 0 when
     the header is hidden, so it always sits flush with the top edge
     of whatever is visible. */
  var lastScrollY = window.scrollY || 0;
  var accumDown = 0;
  var accumUp = 0;
  var HIDE_THRESHOLD = 60;   // px of cumulative downscroll before hiding
  var SHOW_THRESHOLD = 16;   // px of cumulative upscroll before showing
  var ticking = false;

  window.addEventListener('scroll', function() {
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 10);

    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var y = window.scrollY || 0;
      var delta = y - lastScrollY;
      lastScrollY = y;

      // Always show near the top
      if (y < 80) {
        document.body.classList.remove('es-header-hidden');
        accumDown = 0; accumUp = 0;
        return;
      }

      if (delta > 0) {
        accumDown += delta;
        accumUp = 0;
        if (accumDown > HIDE_THRESHOLD) {
          document.body.classList.add('es-header-hidden');
        }
      } else if (delta < 0) {
        accumUp += -delta;
        accumDown = 0;
        if (accumUp > SHOW_THRESHOLD) {
          document.body.classList.remove('es-header-hidden');
        }
      }
    });
  }, { passive: true });

  function setHeaderBottom() {
    // Two CSS variables drive the "sticky top offset" for anything
    // below the header on the page:
    //
    //   --es-header-bottom       = bottom edge of the sticky site header
    //                              (only meaningful on desktop — on
    //                              mobile the site header is NOT sticky
    //                              and this value is clamped to 0 so
    //                              elements that fall back to it don't
    //                              get pushed off-screen by a negative
    //                              value from a scrolled-away header).
    //
    //   --es-coll-header-bottom  = bottom edge of the *lowest* pinned
    //                              sticky bar at the top of the page.
    //                              On desktop that's the site header.
    //                              On mobile that's .es-coll-mob-header
    //                              (collection title / back button /
    //                              count), which is always sticky at
    //                              top: 0.
    var isMobile = window.innerWidth <= 749;
    var headerBottom = 0;
    if (header && !isMobile) {
      headerBottom = Math.max(0, header.getBoundingClientRect().bottom);
    }
    document.documentElement.style.setProperty('--es-header-bottom', headerBottom + 'px');

    var collBottom = headerBottom;
    var mob = document.querySelector('.es-coll-mob-header');
    if (mob) {
      var rect = mob.getBoundingClientRect();
      // Only use the mob-header bottom if the element is actually
      // visible (not display: none on desktop).
      if (rect.height > 0) {
        var mobBottom = Math.max(0, rect.bottom);
        if (mobBottom > collBottom) collBottom = mobBottom;
      }
    }
    document.documentElement.style.setProperty('--es-coll-header-bottom', collBottom + 'px');
  }
  setHeaderBottom();
  window.addEventListener('resize', setHeaderBottom, { passive: true });
  window.addEventListener('scroll', setHeaderBottom, { passive: true });

  // ── Sliding panel navigation ──
  var slideStack = ['root'];

  function getSlide(id) {
    return nav && nav.querySelector('[data-slide="' + id + '"]');
  }

  function openNav() {
    if (!nav) return;
    // Reset to root slide
    slideStack = ['root'];
    nav.querySelectorAll('.es-mobile-nav__slide').forEach(function(s) {
      s.classList.remove('is-active', 'is-behind');
    });
    var root = getSlide('root');
    if (root) root.classList.add('is-active');
    nav.classList.add('is-open');
    nav.setAttribute('aria-hidden', 'false');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeNav() {
    if (!nav) return;
    nav.classList.remove('is-open');
    nav.setAttribute('aria-hidden', 'true');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (toggle) toggle.focus();
    // Reset all slides after transition
    setTimeout(function() {
      slideStack = ['root'];
      nav.querySelectorAll('.es-mobile-nav__slide').forEach(function(s) {
        s.classList.remove('is-active', 'is-behind');
      });
      var root = getSlide('root');
      if (root) root.classList.add('is-active');
    }, 300);
  }

  function openSlide(slideId) {
    var currentId = slideStack[slideStack.length - 1];
    var currentSlide = getSlide(currentId);
    var nextSlide = getSlide(slideId);
    if (!currentSlide || !nextSlide) return;
    currentSlide.classList.remove('is-active');
    currentSlide.classList.add('is-behind');
    nextSlide.classList.add('is-active');
    nextSlide.querySelector('.es-mobile-nav__slide-body').scrollTop = 0;
    slideStack.push(slideId);
  }

  function goBack() {
    if (slideStack.length <= 1) return;
    var currentId = slideStack.pop();
    var prevId = slideStack[slideStack.length - 1];
    var currentSlide = getSlide(currentId);
    var prevSlide = getSlide(prevId);
    if (!currentSlide || !prevSlide) return;
    currentSlide.classList.remove('is-active');
    prevSlide.classList.remove('is-behind');
    prevSlide.classList.add('is-active');
  }

  if (toggle) toggle.addEventListener('click', openNav);
  if (overlay) overlay.addEventListener('click', closeNav);
  // Close menu when clicking anywhere outside the panel
  if (nav) nav.addEventListener('click', function(e) {
    var panel = nav.querySelector('.es-mobile-nav__panel');
    if (panel && !panel.contains(e.target)) closeNav();
  });
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && nav && nav.classList.contains('is-open')) closeNav();
  });

  // Bind slide open buttons
  if (nav) nav.querySelectorAll('[data-open-slide]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      openSlide(this.dataset.openSlide);
    });
  });

  // Bind back buttons
  if (nav) nav.querySelectorAll('[data-go-back]').forEach(function(btn) {
    btn.addEventListener('click', goBack);
  });

  // Bind ALL close buttons (there's one in each slide)
  if (nav) nav.querySelectorAll('.es-mobile-nav__close').forEach(function(btn) {
    btn.addEventListener('click', closeNav);
  });

  // Account popover
  var accountPopover = document.getElementById('es-account-popover');
  var accountBtn = document.getElementById('es-account-btn');
  if (accountBtn && accountPopover) {
    accountBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      var open = accountPopover.classList.toggle('is-open');
      accountBtn.setAttribute('aria-expanded', String(open));
    });
    document.addEventListener('click', function(e) {
      if (accountPopover && !accountPopover.contains(e.target)) {
        accountPopover.classList.remove('is-open');
        accountBtn.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && accountPopover.classList.contains('is-open')) {
        accountPopover.classList.remove('is-open');
        accountBtn.setAttribute('aria-expanded', 'false');
        accountBtn.focus();
      }
    });
  }

  // Wishlist count – update badge elements from localStorage
  function updateWishlistCount() {
    var count = 0;
    try {
      var wishlist = JSON.parse(localStorage.getItem('esWishlist') || '[]');
      count = wishlist.length;
    } catch (_) {}

    var badge = document.getElementById('es-wishlist-badge');
    if (badge) {
      badge.textContent = count;
      badge.setAttribute('data-count', String(count));
    }

    var mobileBadge = document.getElementById('es-mobile-wishlist-count');
    if (mobileBadge) {
      mobileBadge.textContent = count > 0 ? count : '';
      mobileBadge.setAttribute('data-count', String(count));
    }
  }

  updateWishlistCount();
  document.addEventListener('wishlist:updated', updateWishlistCount);

  // Language switcher popover
  var langSwitcher = document.getElementById('es-lang-switcher');
  var langBtn = document.getElementById('es-lang-btn');
  if (langBtn && langSwitcher) {
    langBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      var open = langSwitcher.classList.toggle('is-open');
      langBtn.setAttribute('aria-expanded', String(open));
    });
    document.addEventListener('click', function(e) {
      if (langSwitcher && !langSwitcher.contains(e.target)) {
        langSwitcher.classList.remove('is-open');
        langBtn.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && langSwitcher.classList.contains('is-open')) {
        langSwitcher.classList.remove('is-open');
        langBtn.setAttribute('aria-expanded', 'false');
        langBtn.focus();
      }
    });
  }

  // Mobiel zoek-panel (slide-down onder header)
  var mobileSearch    = document.getElementById('es-mobile-search');
  var mobileInput     = document.getElementById('es-mobile-search-input');
  var mobileClose     = document.getElementById('es-mobile-search-close');
  var mobileScrim     = document.getElementById('es-mobile-search-scrim');
  var searchIconBtn   = document.querySelector('.es-header__search-icon-btn');

  function openMobileSearch() {
    if (!mobileSearch) return;
    mobileSearch.classList.add('is-open');
    if (mobileScrim) mobileScrim.classList.add('is-open');
    // Focus input zodat keyboard opent op iOS/Android
    // Moet synchroon binnen user-gesture (click/touch) blijven, anders blokkeert iOS/Android de keyboard
    if (mobileInput) mobileInput.focus({ preventScroll: true });
  }
  function closeMobileSearch() {
    if (!mobileSearch) return;
    mobileSearch.classList.remove('is-open');
    if (mobileScrim) mobileScrim.classList.remove('is-open');
    if (mobileInput) {
      mobileInput.value = '';
      mobileInput.blur();
    }
    // Reset predictive search state
    var ps = mobileSearch.querySelector('predictive-search');
    if (ps && ps.close) ps.close(true);
  }

  if (searchIconBtn) {
    searchIconBtn.addEventListener('click', function(e) {
      e.preventDefault();
      openMobileSearch();
    });
    function syncSearchBtn() {
      searchIconBtn.style.display = window.innerWidth <= 989 ? 'flex' : 'none';
    }
    syncSearchBtn();
    window.addEventListener('resize', function() {
      syncSearchBtn();
      // Sluit mobiel zoekpanel als window breder wordt dan mobile breakpoint
      if (window.innerWidth > 989 && mobileSearch.classList.contains('is-open')) closeMobileSearch();
    });
  }
  if (mobileClose) mobileClose.addEventListener('click', closeMobileSearch);
  if (mobileScrim) mobileScrim.addEventListener('click', closeMobileSearch);
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && mobileSearch && mobileSearch.classList.contains('is-open')) closeMobileSearch();
  });

  // Typewriter placeholder zoekbalk (desktop)
  (function() {
    var input = document.getElementById('es-search-input');
    if (!input) return;

    // Read the initial placeholder from the input itself (Liquid has
    // already interpolated it server-side into the `placeholder` attr).
    var term     = input.getAttribute('placeholder') || '';
    var charIdx  = 0;
    var typeSpeed = 65;

    function tick() {
      input.setAttribute('placeholder', term.slice(0, charIdx));
      if (charIdx < term.length) {
        charIdx++;
        setTimeout(tick, typeSpeed);
      }
    }

    input.addEventListener('focus', function() { input.setAttribute('placeholder', ''); });
    input.addEventListener('blur',  function() { if (!input.value) { charIdx = 0; tick(); } });

    tick();
  })();
})();
